import { zodResolver } from '@hookform/resolvers/zod';
import { forgotPasswordSchema, type ForgotPasswordInput } from '@pgs/shared';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Chrome } from '@/components/layout/Chrome';
import { api } from '@/lib/api';

export const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordInput>({ resolver: zodResolver(forgotPasswordSchema) });

  const mutation = useMutation({
    mutationFn: async (input: ForgotPasswordInput) => {
      await api.post('/auth/forgot-password', input);
    },
  });

  return (
    <Chrome>
      <div className="mx-auto max-w-md px-6 py-16">
        <div className="rounded bg-white p-8 shadow-cardLg">
          <h1 className="mb-2 text-2xl font-black text-navy">Forgot your password?</h1>
          <p className="mb-6 text-sm text-ink-500">
            Enter the email associated with your account and we'll send you a link to reset your
            password.
          </p>

          {mutation.isSuccess ? (
            <div className="rounded-sm border-l-[3px] border-teal bg-teal-tint p-4 text-sm text-ink-700">
              <p className="font-bold text-navy">Check your inbox.</p>
              <p className="mt-1">
                If an account exists for that email, you'll receive a password reset link shortly.
                The link expires in 60 minutes.
              </p>
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="mt-4 block w-full text-center text-[11px] uppercase tracking-wider text-ink-500 hover:text-navy"
              >
                Back to login
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-4">
              <div>
                <label className="lbl">Email</label>
                <input
                  {...register('email')}
                  type="email"
                  className={`inp ${errors.email ? 'inp-error' : ''}`}
                  placeholder="you@example.com"
                  autoComplete="email"
                />
                {errors.email && <p className="err-msg">{errors.email.message}</p>}
              </div>

              <button type="submit" disabled={mutation.isPending} className="btn-navy w-full">
                {mutation.isPending ? 'Sending…' : 'Send reset link'}
              </button>
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="mt-2 block w-full text-center text-[11px] uppercase tracking-wider text-ink-500 hover:text-navy"
              >
                Back to login
              </button>
            </form>
          )}
        </div>
      </div>
    </Chrome>
  );
};
